import { useState } from "react";
import type { Bootstrap, Bill } from "../../shared/types";
import { api, type BillInput } from "../lib/api";
import { parseMoney, formatDayMonth, daysBetween } from "../lib/format";
import Modal from "./Modal";
import Select from "./Select";
import { useConfirm } from "./ConfirmProvider";

// Add or edit a bill/outgoing, including optional details and (when editing) delete.
export default function BillModal({
  data,
  bill,
  onClose,
  onSaved,
}: {
  data: Bootstrap;
  bill: Bill | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = bill != null;
  const [name, setName] = useState(bill?.name ?? "");
  const [amount, setAmount] = useState(bill ? (bill.amountMinor / 100).toFixed(2) : "");
  const [type, setType] = useState(bill?.type ?? "fixed");
  const [categoryId, setCategoryId] = useState(
    bill ? String(data.categories.find((c) => c.name === bill.categoryName)?.id ?? "") : "",
  );
  const [paidBy, setPaidBy] = useState(bill?.paidById ? String(bill.paidById) : "");
  const [dueDay, setDueDay] = useState(bill?.dueDay != null ? String(bill.dueDay) : "");
  const [accountNumber, setAccountNumber] = useState(bill?.accountNumber ?? "");
  const [website, setWebsite] = useState(bill?.website ?? "");
  const [notes, setNotes] = useState(bill?.notes ?? "");
  const [startDate, setStartDate] = useState(bill?.startDate ?? "");
  const [endDate, setEndDate] = useState(bill?.endDate ?? "");
  // How the bill repeats: every month, once a year (only shows in its renewal
  // month), or just this cycle. Derived when editing: annual from frequency, a
  // one-off from a single-cycle window, otherwise monthly.
  const initRepeat: "monthly" | "annual" | "once" = bill
    ? bill.frequency === "annual"
      ? "annual"
      : bill.startDate && bill.endDate && daysBetween(bill.startDate, bill.endDate) <= 45
        ? "once"
        : "monthly"
    : "monthly";
  const [repeat, setRepeat] = useState<"monthly" | "annual" | "once">(initRepeat);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const cycleStart = data.summary.cycleStart;
  const cycleEnd = data.summary.cycleEnd;

  async function save() {
    const minor = parseMoney(amount);
    if (!name.trim()) return setError("Give the bill a name");
    if (minor == null || minor <= 0) return setError("Enter a valid amount");
    if (repeat === "annual" && !startDate) return setError("Pick the renewal date for a yearly bill");

    // Dates by cadence: yearly anchors on the renewal date; this-cycle-only is
    // bounded to the current cycle; monthly keeps whatever manual bounds are set.
    const sd = repeat === "once" ? cycleStart : repeat === "annual" ? startDate : startDate || null;
    const ed = repeat === "once" ? cycleEnd : endDate || null;
    // Yearly bills take their due day from the renewal date.
    const day = repeat === "annual" ? Number(startDate.slice(8, 10)) : dueDay ? Number(dueDay) : null;

    const payload: BillInput = {
      name: name.trim(),
      amountMinor: minor,
      type,
      frequency: repeat === "annual" ? "annual" : repeat === "once" ? "once" : "monthly",
      categoryId: categoryId ? Number(categoryId) : null,
      paidBy: paidBy ? Number(paidBy) : null,
      dueDay: day,
      accountNumber: accountNumber.trim() || null,
      website: website.trim() || null,
      notes: notes.trim() || null,
      startDate: sd,
      endDate: ed,
    };
    setSaving(true);
    try {
      if (editing) await api.updateBill(bill.id, payload);
      else await api.createBill(payload);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  const ask = useConfirm();

  async function remove() {
    if (!bill) return;
    if (!(await ask({ title: "Archive bill?", message: `Archive "${bill.name}"? It stays in your history but stops counting going forward.`, confirmLabel: "Archive" }))) return;
    setSaving(true);
    try {
      await api.deleteBill(bill.id);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title={editing ? "Edit bill" : "Add bill"} onClose={onClose}>
      <div className="form">
        <label className="field">
          <span className="field__label">Description</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Council Tax" autoFocus />
        </label>
        <label className="field">
          <span className="field__label">Amount (£)</span>
          <input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="281.30" />
        </label>
        <div className="form__row">
          <div className="field">
            <span className="field__label">Category</span>
            <Select
              value={categoryId}
              onChange={setCategoryId}
              placeholder="—"
              ariaLabel="Category"
              options={[{ value: "", label: "—" }, ...data.categories.map((c) => ({ value: String(c.id), label: c.name }))]}
            />
          </div>
          <div className="field">
            <span className="field__label">Type</span>
            <Select
              value={type}
              onChange={setType}
              ariaLabel="Type"
              options={data.types.filter((t) => t.key !== "plan").map((t) => ({ value: t.key, label: t.label }))}
            />
          </div>
        </div>
        <div className="form__row">
          <div className="field">
            <span className="field__label">Paid by</span>
            <Select
              value={paidBy}
              onChange={setPaidBy}
              placeholder="—"
              ariaLabel="Paid by"
              options={[{ value: "", label: "—" }, ...data.household.members.map((m) => ({ value: String(m.id), label: m.name }))]}
            />
          </div>
          <label className="field">
            <span className="field__label">Due day of month</span>
            <input className="input" inputMode="numeric" value={dueDay} onChange={(e) => setDueDay(e.target.value)} placeholder="1" />
          </label>
        </div>

        <p className="form__section">Optional details</p>
        <label className="field">
          <span className="field__label">Details</span>
          <input className="input" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="Account no., reference, sort code…" />
        </label>
        <label className="field">
          <span className="field__label">Website</span>
          <input className="input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="e.g. britishgas.co.uk" />
        </label>
        <label className="field">
          <span className="field__label">Notes</span>
          <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything worth remembering about this bill" />
        </label>
        <div className="field">
          <span className="field__label">How often?</span>
          <Select
            value={repeat}
            onChange={(v) => setRepeat(v as "monthly" | "annual" | "once")}
            ariaLabel="How often"
            options={[
              { value: "monthly", label: "Every month" },
              { value: "annual", label: "Once a year" },
              { value: "once", label: "Just this month" },
            ]}
          />
        </div>

        {repeat === "monthly" && (
          <>
            <div className="form__row">
              <label className="field">
                <span className="field__label">Start date (optional)</span>
                <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <label className="field">
                <span className="field__label">End date (optional)</span>
                <input className="input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </label>
            </div>
            <p className="field__hint">Leave dates blank for an ongoing bill. After the end date it stops counting.</p>
          </>
        )}
        {repeat === "annual" && (
          <>
            <div className="form__row">
              <label className="field">
                <span className="field__label">Renews on</span>
                <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <label className="field">
                <span className="field__label">End date (optional)</span>
                <input className="input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </label>
            </div>
            <p className="field__hint">Only shows in its renewal month each year, for the full amount. Leave end date blank if ongoing.</p>
          </>
        )}
        {repeat === "once" && (
          <p className="field__hint">Counts once, for this cycle only ({formatDayMonth(cycleStart)} – {formatDayMonth(cycleEnd)}).</p>
        )}

        {error && <p className="form__error">{error}</p>}
        <div className="form__actions form__actions--split">
          {editing ? (
            <button className="btn btn--danger" onClick={remove} disabled={saving}>Archive</button>
          ) : <span />}
          <div className="form__actions">
            <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn--primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save bill" : "Add bill"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
